import { z } from "zod"

/**
 * The engagement analytics surface (PRD: engagement-analysis-layer, phase 7).
 * Single definition of the wire shapes: the API routes import these and the
 * api-client parses responses through them, so the two cannot drift.
 *
 * **Amounts are integer cents, not decimal strings.** Every other read
 * surface in this product hands amounts over as strings because it only
 * displays them. Analytics does arithmetic on the client: the flow-of-funds
 * residual link, each row's share of a total, the balance carry-forward. In
 * integer cents that arithmetic is exact; in floats it drifts, and drift on
 * an accountant's workpaper is the failure this whole PRD exists to avoid.
 * `centsToDecimalString` formats them for display.
 *
 * **One currency at a time, never converted.** A workspace whose rows span
 * currencies gets a switcher and each view aggregates one currency; we hold
 * no rates and an invented rate is worse than a filter. `null` is the
 * currency of rows whose statement never stated one: honestly labelled, not
 * silently folded into the dominant code.
 */

const monthPattern = /^\d{4}-\d{2}$/
const datePattern = /^\d{4}-\d{2}-\d{2}$/

/** The month bucket size for the flow-over-time view. */
export const ApiAnalyticsGranularitySchema = z.enum(["month", "week"])

export type ApiAnalyticsGranularity = z.infer<
  typeof ApiAnalyticsGranularitySchema
>

/**
 * The sentinel the currency query parameter uses for "the rows whose
 * statement stated no usable currency". A bare absent parameter cannot
 * express it, because absent means "pick the dominant currency".
 */
export const analyticsUnstatedCurrency = "unstated"

export const ApiAnalyticsQuerySchema = z.object({
  /** Inclusive first month, "YYYY-MM". Absent means the whole engagement. */
  from: z.string().regex(monthPattern).optional(),
  /** Inclusive last month, "YYYY-MM". */
  to: z.string().regex(monthPattern).optional(),
  /**
   * An ISO 4217 code, or the `unstated` sentinel. Absent means the server
   * picks the currency holding the most rows and echoes its choice back.
   */
  currency: z.string().min(1).max(12).optional(),
  granularity: ApiAnalyticsGranularitySchema.optional(),
})

export type ApiAnalyticsQuery = z.infer<typeof ApiAnalyticsQuerySchema>

export const ApiAnalyticsCurrencySchema = z.object({
  /** Null for rows whose statement stated no usable currency code. */
  code: z.string().nullable(),
  rowCount: z.number().int(),
})

export type ApiAnalyticsCurrency = z.infer<typeof ApiAnalyticsCurrencySchema>

export const ApiAnalyticsBalancePointSchema = z.object({
  month: z.string().regex(monthPattern),
  /** The closing balance of the latest period ending in this month. */
  closingCents: z.number().int(),
})

export type ApiAnalyticsBalancePoint = z.infer<
  typeof ApiAnalyticsBalancePointSchema
>

export const ApiAnalyticsBalanceAccountSchema = z.object({
  accountId: z.string().uuid(),
  /**
   * The stored statement polarity verdict for this account's periods, or
   * null when no statement carries one. A liability account's stated
   * balance is what is owed, so the combined net series negates it.
   */
  direction: z.enum(["asset", "liability"]).nullable(),
  points: z.array(ApiAnalyticsBalancePointSchema),
})

export type ApiAnalyticsBalanceAccount = z.infer<
  typeof ApiAnalyticsBalanceAccountSchema
>

/**
 * The raw balance readings. The dense per-month series, the carry-forward
 * across months with no statement, and the combined net line all derive on
 * the client through `deriveBalanceSeries`, so changing the range redraws
 * without a round trip.
 */
export const ApiAnalyticsBalanceSchema = z.object({
  accounts: z.array(ApiAnalyticsBalanceAccountSchema),
})

export type ApiAnalyticsBalance = z.infer<typeof ApiAnalyticsBalanceSchema>

export const ApiAnalyticsFlowBucketSchema = z.object({
  /** ISO date of the bucket's first day. */
  bucketStart: z.string().regex(datePattern),
  /** Money arriving, read through each statement's polarity verdict. */
  incomeCents: z.number().int(),
  /** Money leaving, as a positive number. */
  expenseCents: z.number().int(),
  rowCount: z.number().int(),
})

export type ApiAnalyticsFlowBucket = z.infer<
  typeof ApiAnalyticsFlowBucketSchema
>

export const ApiAnalyticsFlowSchema = z.object({
  buckets: z.array(ApiAnalyticsFlowBucketSchema),
  totals: z.object({
    incomeCents: z.number().int(),
    expenseCents: z.number().int(),
    rowCount: z.number().int(),
  }),
})

export type ApiAnalyticsFlow = z.infer<typeof ApiAnalyticsFlowSchema>

export const ApiAnalyticsCategoryGroupSchema = z.object({
  /** Null for the uncategorised group. */
  categoryId: z.string().uuid().nullable(),
  name: z.string(),
  /** Positive: money arriving for an income group, leaving for an expense one. */
  amountCents: z.number().int(),
  rowCount: z.number().int(),
})

export type ApiAnalyticsCategoryGroup = z.infer<
  typeof ApiAnalyticsCategoryGroupSchema
>

/**
 * Transfer-kind categories and paired transfer rows, reported as their own
 * band. Money moving between an engagement's own accounts nets to zero, so
 * these are counted in neither income nor expense; both gross sides are
 * reported so an imbalance stays visible rather than being absorbed.
 */
export const ApiAnalyticsTransferGroupSchema = z.object({
  categoryId: z.string().uuid().nullable(),
  name: z.string(),
  inflowCents: z.number().int(),
  outflowCents: z.number().int(),
  rowCount: z.number().int(),
})

export type ApiAnalyticsTransferGroup = z.infer<
  typeof ApiAnalyticsTransferGroupSchema
>

export const ApiAnalyticsCategoriesSchema = z.object({
  income: z.array(ApiAnalyticsCategoryGroupSchema),
  expense: z.array(ApiAnalyticsCategoryGroupSchema),
  transfers: z.array(ApiAnalyticsTransferGroupSchema),
})

export type ApiAnalyticsCategories = z.infer<
  typeof ApiAnalyticsCategoriesSchema
>

export const ApiAnalyticsVendorGroupSchema = z.object({
  /** Null when description cleanup has not derived a vendor identity. */
  counterpartyKey: z.string().nullable(),
  label: z.string(),
  inflowCents: z.number().int(),
  outflowCents: z.number().int(),
  rowCount: z.number().int(),
})

export type ApiAnalyticsVendorGroup = z.infer<
  typeof ApiAnalyticsVendorGroupSchema
>

export const ApiAnalyticsVendorsSchema = z.object({
  groups: z.array(ApiAnalyticsVendorGroupSchema),
  /**
   * Every distinct vendor in the window, including any past the returned
   * cap. Reported so a cap can never read as complete coverage.
   */
  totalGroups: z.number().int(),
  /** The residual the returned groups leave out, so totals still reconcile. */
  hiddenInflowCents: z.number().int(),
  hiddenOutflowCents: z.number().int(),
  hiddenRowCount: z.number().int(),
})

export type ApiAnalyticsVendors = z.infer<typeof ApiAnalyticsVendorsSchema>

/** Income category into an account, or an account out to an expense category. */
export const ApiAnalyticsFlowLinkSchema = z.object({
  categoryId: z.string().uuid().nullable(),
  categoryName: z.string(),
  accountId: z.string().uuid(),
  amountCents: z.number().int(),
  rowCount: z.number().int(),
})

export type ApiAnalyticsFlowLink = z.infer<typeof ApiAnalyticsFlowLinkSchema>

/**
 * A paired transfer between two of the engagement's own accounts. Its own
 * band rather than a link inside the layered diagram: account-to-account
 * edges can point both ways between the same two accounts, and a cycle is
 * not something a Sankey can lay out honestly.
 */
export const ApiAnalyticsTransferLinkSchema = z.object({
  fromAccountId: z.string().uuid(),
  toAccountId: z.string().uuid(),
  amountCents: z.number().int(),
  pairCount: z.number().int(),
})

export type ApiAnalyticsTransferLink = z.infer<
  typeof ApiAnalyticsTransferLinkSchema
>

export const ApiAnalyticsSankeySchema = z.object({
  incomeLinks: z.array(ApiAnalyticsFlowLinkSchema),
  expenseLinks: z.array(ApiAnalyticsFlowLinkSchema),
  transferLinks: z.array(ApiAnalyticsTransferLinkSchema),
})

export type ApiAnalyticsSankey = z.infer<typeof ApiAnalyticsSankeySchema>

export const ApiAnalyticsResponseSchema = z.object({
  /** The currency every amount below is in; null means "not stated". */
  currency: z.string().nullable(),
  /** Every currency present in the workspace, so the switcher can appear. */
  availableCurrencies: z.array(ApiAnalyticsCurrencySchema),
  granularity: ApiAnalyticsGranularitySchema,
  /** The resolved window, as ISO dates. Null on a side means unbounded. */
  range: z.object({
    from: z.string().regex(datePattern).nullable(),
    to: z.string().regex(datePattern).nullable(),
  }),
  /**
   * Included rows whose statement carries no polarity verdict, so their
   * signs were read as an asset statement's. Surfaced rather than hidden:
   * statements read before the accuracy milestone have no verdict, and a
   * card statement among them would be on the wrong side.
   */
  unstatedDirectionRowCount: z.number().int(),
  balance: ApiAnalyticsBalanceSchema,
  flow: ApiAnalyticsFlowSchema,
  categories: ApiAnalyticsCategoriesSchema,
  vendors: ApiAnalyticsVendorsSchema,
  sankey: ApiAnalyticsSankeySchema,
})

export type ApiAnalyticsResponse = z.infer<typeof ApiAnalyticsResponseSchema>

/** The row table beneath the charts, and what a Sankey link drills into. */
export const ApiAnalyticsRowsQuerySchema = z.object({
  from: z.string().regex(monthPattern).optional(),
  to: z.string().regex(monthPattern).optional(),
  currency: z.string().min(1).max(12).optional(),
  /** "none" filters to the uncategorised rows. */
  categoryId: z.string().optional(),
  accountId: z.string().uuid().optional(),
  /** "none" filters to rows with no derived vendor identity. */
  counterpartyKey: z.string().optional(),
  direction: z.enum(["income", "expense"]).optional(),
  limit: z.coerce.number().int().min(1).max(500).optional(),
  offset: z.coerce.number().int().min(0).optional(),
})

export type ApiAnalyticsRowsQuery = z.infer<typeof ApiAnalyticsRowsQuerySchema>

export const ApiAnalyticsRowSchema = z.object({
  documentId: z.string().uuid(),
  recordType: z.string(),
  recordIndex: z.number().int(),
  recordDate: z.string().nullable(),
  description: z.string().nullable(),
  /** Signed as stored, so the row table matches the statement. */
  amountCents: z.number().int(),
  /** Signed as flow: positive is money arriving, after the polarity verdict. */
  flowCents: z.number().int(),
  currency: z.string().nullable(),
  accountId: z.string().uuid().nullable(),
  categoryId: z.string().uuid().nullable(),
  categoryName: z.string().nullable(),
  counterpartyKey: z.string().nullable(),
})

export type ApiAnalyticsRow = z.infer<typeof ApiAnalyticsRowSchema>

export const ApiAnalyticsRowsResponseSchema = z.object({
  rows: z.array(ApiAnalyticsRowSchema),
  totalRows: z.number().int(),
  /** Totals over EVERY matching row, not just the returned page. */
  totals: z.object({
    incomeCents: z.number().int(),
    expenseCents: z.number().int(),
  }),
})

export type ApiAnalyticsRowsResponse = z.infer<
  typeof ApiAnalyticsRowsResponseSchema
>
